import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchNodeTree, fetchNodeSettings, updateNodeSetting, createNode as createNodeApi, fileDelete, fileGetContent, fileSaveContent } from '../services/api';

/** Hardcoded fallback when no ``_defaults`` section exists in node_setting.json. */
const HARD_DEFAULTS = {
  file_icon: '\u{1F4C4}',  // 📄
  dir_icon:  '\u{1F4C1}',  // 📁
};

/**
 * Hook for managing node tree and per-node settings.
 *
 * Settings are stored in ``node_setting.json`` in the git repo.
 * Node types are resolved structurally: ``.md`` files → ``"markdown"``.
 *
 * The ``tree`` is a flat map of folder-path → children, built from a
 * non-recursive listing of the repository root — subfolders are
 * loaded on demand via ``fetchChildren``.
 *
 * @returns {{
 *   settings: Object<string,Object<string,string>>,
 *   loading: boolean,
 *   setNodeSetting: (path: string, key: string, value: string) => Promise<void>,
 *   getNodeSetting: (path: string, key: string) => string | undefined,
 *   icons: Object<string,string>,     // flat path → icon (excludes _defaults)
 *   defaults: {file_icon: string, dir_icon: string},
 *   quickAccessNodes: Array<{path: string, name: string, icon: string, type: string}>,
 *   hiddenPaths: Set<string>,        // folder paths with hidden === "true"
 *   nodeTypes: Object<string,string>, // flat path → type (from structural detection)
 *   tree: Object<string,Array>,       // folder-path → children (from directory listing)
 *   error: string | null,
 *   refresh: () => Promise<void>,
 *   createNode: (path: string, type: string, content?: string) => Promise<Object>,
 *   deleteNode: (path: string) => Promise<void>,
 *   renameNode: (oldPath: string, newPath: string) => Promise<{content, sha}>,
 * }}
 */
export default function useNodeSettings() {
  const [allSettings, setAllSettings] = useState({});
  const [tree, setTree] = useState({});
  const [nodeTypes, setNodeTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const allRef = useRef(allSettings);
  const treeRef = useRef(tree);
  const nodeTypesRef = useRef(nodeTypes);

  useEffect(() => { allRef.current = allSettings; }, [allSettings]);
  useEffect(() => { treeRef.current = tree; }, [tree]);
  useEffect(() => { nodeTypesRef.current = nodeTypes; }, [nodeTypes]);

  // Load node tree + settings independently on mount
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [treeData, settingsData] = await Promise.all([
        fetchNodeTree(),
        fetchNodeSettings(),
      ]);
      setTree(treeData.tree || {});
      setNodeTypes(treeData.nodeTypes || {});
      setAllSettings(settingsData.settings || {});
    } catch (err) {
      console.error('Failed to load node tree:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Set a single key for a node and persist — optimistic update for instant UI
  const setNodeSetting = useCallback(async (nodePath, key, value) => {
    const trimmed = (value || '').trim();
    const payload = { [key]: trimmed };

    // Snapshot previous settings for rollback on failure
    const prevSettings = allRef.current;

    // Optimistic update — apply change to local state immediately
    setAllSettings(prev => {
      const next = {};
      for (const p of Object.keys(prev)) {
        next[p] = { ...prev[p] };
      }
      if (!next[nodePath]) next[nodePath] = {};
      if (trimmed) {
        next[nodePath][key] = trimmed;
      } else {
        delete next[nodePath][key];
        if (Object.keys(next[nodePath]).length === 0) {
          delete next[nodePath];
        }
      }
      return next;
    });

    try {
      const data = await updateNodeSetting(nodePath, payload);
      // Merge in the authoritative server response (non-destructive)
      const serverSettings = data.settings?.[nodePath] || {};
      setAllSettings(prev => {
        const next = {};
        for (const p of Object.keys(prev)) {
          next[p] = { ...prev[p] };
        }
        if (Object.keys(serverSettings).length > 0) {
          next[nodePath] = { ...next[nodePath], ...serverSettings };
        } else if (next[nodePath] && Object.keys(next[nodePath]).length === 0) {
          delete next[nodePath];
        }
        return next;
      });
      if (data.tree && Object.keys(data.tree).length > 0) {
        setTree(data.tree);
      }
      if (data.nodeTypes && Object.keys(data.nodeTypes).length > 0) {
        setNodeTypes(data.nodeTypes);
      }
    } catch (err) {
      // Rollback on failure
      setAllSettings(prevSettings);
      console.error('Failed to update node setting:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Get a specific setting value for a node
  const getNodeSetting = useCallback((nodePath, key) => {
    return allSettings[nodePath]?.[key];
  }, [allSettings]);

  // Create a new node in one operation.
  //
  // The node is inserted into `tree`/`nodeTypes` *before* the request is
  // sent (optimistic), keyed by its parent folder, so the UI reflects the
  // new node immediately instead of waiting on the synchronous git write.
  // The backend no longer returns a tree for this endpoint, so on success
  // we keep the local insert; on failure we roll it back.
  const createNode = useCallback(async (path, type, content = '') => {
    const filePath = path.endsWith('.md') ? path : path + '.md';
    const name = filePath.split('/').pop() || filePath;
    const parentDir = filePath.includes('/')
      ? filePath.slice(0, filePath.lastIndexOf('/'))
      : '';

    setError(null);

    // Optimistic insert — add the new node to the parent folder's children
    // and register its type.  The `tree` map is keyed by folder path.
    const optimisticChild = { name, type: 'blob', path: filePath };
    setTree(prev => {
      const next = { ...prev };
      const children = [...(next[parentDir] || [])];
      if (!children.some(c => c.path === filePath)) {
        children.push(optimisticChild);
      }
      next[parentDir] = children;
      return next;
    });
    setNodeTypes(prev => ({ ...prev, [filePath]: 'markdown' }));

    try {
      const data = await createNodeApi(path, type, content);
      // Reconcile with the server.  The create endpoint returns empty
      // tree/nodeTypes (optimistic insert is authoritative), so this is a
      // no-op today — kept for when settings or tree ever come back.
      if (data.settings && Object.keys(data.settings).length > 0) {
        setAllSettings(data.settings);
      }
      if (data.tree && Object.keys(data.tree).length > 0) {
        setTree(prev => ({ ...prev, ...data.tree }));
      }
      if (data.nodeTypes && Object.keys(data.nodeTypes).length > 0) {
        setNodeTypes(prev => ({ ...prev, ...data.nodeTypes }));
      }
      return data;
    } catch (err) {
      // Rollback the optimistic insert on failure.
      setTree(prev => {
        const next = { ...prev };
        const children = (next[parentDir] || []).filter(c => c.path !== filePath);
        if (children.length > 0) {
          next[parentDir] = children;
        } else {
          delete next[parentDir];
        }
        return next;
      });
      setNodeTypes(prev => {
        const next = { ...prev };
        delete next[filePath];
        return next;
      });
      console.error('Failed to create node:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Create a new folder, optimistically inserting a `tree` child into the
  // parent's children before the git write (a .gitkeep anchor) completes.
  const createFolder = useCallback(async (path) => {
    const folderPath = path.replace(/\/+$/, ''); // strip any trailing slash
    const name = folderPath.split('/').pop() || folderPath;
    const parentDir = folderPath.includes('/')
      ? folderPath.slice(0, folderPath.lastIndexOf('/'))
      : '';

    setError(null);

    const optimisticChild = { name, type: 'tree', path: folderPath };
    setTree(prev => {
      const next = { ...prev };
      const children = [...(next[parentDir] || [])];
      if (!children.some(c => c.path === folderPath)) {
        children.push(optimisticChild);
      }
      next[parentDir] = children;
      return next;
    });

    try {
      const data = await createNodeApi(folderPath, 'folder', '');
      if (data.tree && Object.keys(data.tree).length > 0) {
        setTree(prev => ({ ...prev, ...data.tree }));
      }
      return data;
    } catch (err) {
      // Roll back the optimistic insert on failure.
      setTree(prev => {
        const next = { ...prev };
        const children = (next[parentDir] || []).filter(c => c.path !== folderPath);
        if (children.length > 0) {
          next[parentDir] = children;
        } else {
          delete next[parentDir];
        }
        return next;
      });
      console.error('Failed to create folder:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Delete a node (file or folder), optimistically removing it from the
  // tree/nodeTypes/settings before the synchronous git delete completes.
  // Rolls back only this node's removal on failure.
  const deleteNode = useCallback(async (path) => {
    const parentDir = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';

    // Capture exactly what we're removing, for surgical rollback.
    const prevFolderChildren = treeRef.current[path];       // folder children (undefined for files)
    const prevChild = (treeRef.current[parentDir] || []).find(c => c.path === path);
    const prevType = nodeTypesRef.current[path];
    const prevSettingsEntry = allRef.current[path];

    setError(null);

    // Optimistic removal.
    setTree(prev => {
      const next = { ...prev };
      delete next[path]; // the folder's own children entry (no-op for files)
      const children = (next[parentDir] || []).filter(c => c.path !== path);
      if (children.length > 0) {
        next[parentDir] = children;
      } else {
        delete next[parentDir];
      }
      return next;
    });
    setNodeTypes(prev => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
    setAllSettings(prev => {
      const next = { ...prev };
      delete next[path];
      return next;
    });

    try {
      await fileDelete(path);

      // Drop the orphaned settings entry server-side.  One write clears all
      // keys (empty value = remove) — replaces the old per-key loop.
      const settingsKeys = Object.keys(prevSettingsEntry || {});
      if (settingsKeys.length > 0) {
        const payload = {};
        settingsKeys.forEach(k => { payload[k] = ''; });
        try {
          await updateNodeSetting(path, payload);
        } catch (_) { /* best-effort: settings file may not exist */ }
      }
    } catch (err) {
      // Roll back this node's optimistic removal (not a full snapshot, so a
      // concurrent operation on another node isn't clobbered).
      setTree(prev => {
        const next = { ...prev };
        if (prevFolderChildren !== undefined) next[path] = prevFolderChildren;
        if (prevChild !== undefined) {
          const children = [...(next[parentDir] || [])];
          if (!children.some(c => c.path === path)) children.push(prevChild);
          next[parentDir] = children;
        }
        return next;
      });
      setNodeTypes(prev => {
        const next = { ...prev };
        if (prevType !== undefined) next[path] = prevType; else delete next[path];
        return next;
      });
      setAllSettings(prev => {
        const next = { ...prev };
        if (prevSettingsEntry !== undefined) next[path] = prevSettingsEntry; else delete next[path];
        return next;
      });
      console.error('Failed to delete node:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Rename a node within its folder, optimistically swapping its name/path in
  // the tree before the file-level rename (create new + delete old) completes.
  // Returns the old file's {content, sha} (content_sha) so the caller can
  // migrate any pending outbox entry.  Rolls back the tree rename on failure.
  const renameNode = useCallback(async (oldPath, newPath) => {
    const oldName = oldPath.split('/').pop();
    const newName = newPath.split('/').pop();
    const parentDir = oldPath.includes('/') ? oldPath.slice(0, oldPath.lastIndexOf('/')) : '';

    // Capture the old node for surgical rollback.
    const prevChild = (treeRef.current[parentDir] || []).find(c => c.path === oldPath);
    const prevType = nodeTypesRef.current[oldPath];
    const prevSettingsEntry = allRef.current[oldPath];

    setError(null);

    // Optimistic rename: swap name/path in the parent's children and move the
    // type/settings entries from oldPath → newPath.
    setTree(prev => {
      const next = { ...prev };
      const children = (next[parentDir] || []).map(c =>
        c.path === oldPath ? { ...c, name: newName, path: newPath } : c
      );
      if (children.length > 0) next[parentDir] = children;
      return next;
    });
    setNodeTypes(prev => {
      const next = { ...prev };
      const t = next[oldPath];
      delete next[oldPath];
      if (t !== undefined) next[newPath] = t;
      return next;
    });
    setAllSettings(prev => {
      const next = { ...prev };
      const s = next[oldPath];
      delete next[oldPath];
      if (s !== undefined) next[newPath] = s;
      return next;
    });

    try {
      // Get current content — always fetch from server
      const result = await fileGetContent(oldPath);
      const content = result.content || '';

      // Create new file + delete old
      await fileSaveContent(newPath, content, null);
      try { await fileDelete(oldPath); } catch (_) { /* best-effort */ }

      return { content, sha: result.sha };
    } catch (err) {
      // Roll back the optimistic rename (surgical, not a full snapshot).
      setTree(prev => {
        const next = { ...prev };
        const children = (next[parentDir] || []).map(c =>
          (c.path === newPath && prevChild) ? prevChild : c
        );
        if (children.length > 0) next[parentDir] = children;
        else delete next[parentDir];
        return next;
      });
      setNodeTypes(prev => {
        const next = { ...prev };
        delete next[newPath];
        if (prevType !== undefined) next[oldPath] = prevType;
        return next;
      });
      setAllSettings(prev => {
        const next = { ...prev };
        delete next[newPath];
        if (prevSettingsEntry !== undefined) next[oldPath] = prevSettingsEntry;
        return next;
      });
      console.error('Failed to rename node:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // ── Defaults ──────────────────────────────────────────────────
  const defaults = useMemo(() => {
    const ds = allSettings['_defaults'] || {};
    return {
      file_icon: ds.file_icon || HARD_DEFAULTS.file_icon,
      dir_icon: ds.dir_icon || HARD_DEFAULTS.dir_icon,
    };
  }, [allSettings]);

  // ── Flat icon index (excludes _defaults) ─────────────────────
  const icons = useMemo(() => {
    const idx = {};
    for (const [path, entries] of Object.entries(allSettings)) {
      if (path === '_defaults') continue;
      if (entries.icon) {
        idx[path] = entries.icon;
      }
    }
    return idx;
  }, [allSettings]);

  // ── Hidden folder paths (excludes _defaults) ─────────────────
  // Folders whose settings carry `hidden === "true"` are omitted from the
  // tree unless the global "show all" toggle is on.
  const hiddenPaths = useMemo(() => {
    const set = new Set();
    for (const [path, entries] of Object.entries(allSettings)) {
      if (path === '_defaults') continue;
      if (entries.hidden === 'true') set.add(path);
    }
    return set;
  }, [allSettings]);

  // ── Quick-access nodes (excludes _defaults) ──────────────────
  const quickAccessNodes = useMemo(() => {
    const nodes = [];
    for (const [path, entries] of Object.entries(allSettings)) {
      if (path === '_defaults') continue;
      if (path.endsWith('.json')) continue; // JulySpace views — notes sidebar is .md-only
      if (entries.quick_access === 'true') {
        const name = path.split('/').pop() || path;
        // Folders are keys in the `tree` map; files carry a `nodeTypes` entry.
        const isFolder = !nodeTypes[path] && tree[path] !== undefined;
        nodes.push({
          path,
          name,
          icon: entries.icon || (isFolder ? defaults.dir_icon : defaults.file_icon),
          type: isFolder ? 'folder' : 'file',
        });
      }
    }
    return nodes;
  }, [allSettings, defaults.file_icon, defaults.dir_icon, nodeTypes, tree]);

  // Fetch children of a specific folder on demand (lazy loading).
  // Merges the result into the existing tree and nodeTypes state.
  const fetchChildren = useCallback(async (folderPath) => {
    setError(null);
    try {
      const data = await fetchNodeTree(folderPath);
      // Merge tree: only add new entries (don't overwrite existing)
      setTree(prev => {
        const next = { ...prev };
        for (const [key, children] of Object.entries(data.tree || {})) {
          // Only set if not already cached (preserves previously loaded folders)
          if (!next[key]) {
            next[key] = children;
          }
        }
        return next;
      });
      // Merge nodeTypes
      if (data.nodeTypes && Object.keys(data.nodeTypes).length > 0) {
        setNodeTypes(prev => ({ ...prev, ...data.nodeTypes }));
      }
      // Return the first (and only) key's children for convenience
      const children = Object.values(data.tree || {})[0] || [];
      return children;
    } catch (err) {
      console.error('Failed to fetch folder children:', err);
      setError(err.message);
      return [];
    }
  }, []);

  return {
    settings: allSettings,
    loading,
    setNodeSetting,
    getNodeSetting,
    icons,
    defaults,
    quickAccessNodes,
    hiddenPaths,
    nodeTypes,
    tree,
    error,
    refresh,
    createNode,
    createFolder,
    deleteNode,
    renameNode,
    fetchChildren,
  };
}
